import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Chip,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Tooltip,
  Divider,
  InputAdornment,
  Snackbar,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Refresh as RefreshIcon,
  CalendarToday,
  LocationOn,
  ConfirmationNumber,
  People,
  AttachMoney,
  Build as SetupIcon,
  Publish as PublishIcon,
  Visibility as ViewIcon,
  QrCode as QrIcon,
  Nfc as NfcIcon,
  EmojiEvents as TrophyIcon,
  Chat as ChatIcon,
  Store as VendorIcon,
  LocalOffer as PromoIcon,
  AccountBalanceWallet as WalletIcon,
  ConfirmationNumber as TicketIcon,
  Storefront,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Article as PaperIcon,
  CardGiftcard as ScholarshipIcon,
  CheckCircleOutline as ApproveIcon,
  HighlightOff as RejectIcon,
  WorkspacePremium as PlanIcon,
  RocketLaunch as StarterIcon,
  AutoAwesome as ProIcon,
  Diamond as EnterpriseIcon,
  ArrowDropDown as DropdownIcon,
  BeachAccess as BeachAccessIcon,
  Hotel as HotelIcon,
  FolderOpen as FolderOpenIcon,
  Sms as SmsIcon,
  Email as MailIcon,
  Inventory2 as StockIcon,
  SellOutlined as EsgotadoIcon,
  TravelExplore as CrawlerIcon,
} from '@mui/icons-material';
import eventsService, { Event, EventInput, EventStats, Vendor, PromoCode, Wristband } from '../../services/events.service';
import entitiesService from '../../services/entities.service';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import HeroSlidesPage from './HeroSlides';

const CATEGORIAS = [
  { value: 'CONCERT', label: 'Concerto' },
  { value: 'FESTIVAL', label: 'Festival' },
  { value: 'CONFERENCE', label: 'Conferência' },
  { value: 'WORKSHOP', label: 'Workshop' },
  { value: 'SPORTS', label: 'Desporto' },
  { value: 'THEATER', label: 'Teatro' },
  { value: 'EXHIBITION', label: 'Exposição' },
  { value: 'PARTY', label: 'Festa' },
  { value: 'OTHER', label: 'Outro' },
];

const TIPOS = [
  { value: 'PRESENCIAL', label: 'Presencial' },
  { value: 'ONLINE', label: 'Online' },
  { value: 'HIBRIDO', label: 'Híbrido' },
];

const STATUS_OPTIONS = [
  { value: 'PENDING_REVIEW', label: 'Aguarda Aprovação', color: 'warning' as const },
  { value: 'DRAFT', label: 'Rascunho', color: 'default' as const },
  { value: 'PUBLISHED', label: 'Publicado', color: 'success' as const },
  { value: 'REJECTED', label: 'Rejeitado', color: 'error' as const },
  { value: 'CANCELLED', label: 'Cancelado', color: 'error' as const },
  { value: 'COMPLETED', label: 'Finalizado', color: 'info' as const },
  { value: 'POSTPONED', label: 'Adiado', color: 'warning' as const },
];

export type Plano = string; // Dynamic — codes come from pg_planos

const PLANO_OPTIONS: { value: string; label: string; color: string; bg: string; description: string }[] = [
  { value: 'STARTER',    label: 'Starter',    color: '#64748b', bg: '#f1f5f9', description: 'Eventos gratuitos, sem bilhetes pagos' },
  { value: 'PRO',        label: 'Pro',        color: '#7c3aed', bg: '#ede9fe', description: 'Bilhetes pagos, convites, chat, waitlist' },
  { value: 'BUSINESS',   label: 'Business',   color: '#0369a1', bg: '#e0f2fe', description: 'Para equipas e eventos de maior escala' },
  { value: 'ENTERPRISE', label: 'Enterprise', color: '#b45309', bg: '#fef3c7', description: 'NFC, cashless, gamificação + tudo do Business' },
];

const CV_TZ = 'Atlantic/Cape_Verde';

const Events: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'PFADM';

  // Main page tab state
  const [mainTab, setMainTab] = useState(0);

  // Events list state
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<EventStats | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  // Search & filter
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  const [crawlerLoading, setCrawlerLoading] = useState(false);
  const [seedLoading, setSeedLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importText, setImportText] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<{ eventId: number; warnings: string[]; message: string } | null>(null);
  const [editDialog, setEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [dialogTabValue, setDialogTabValue] = useState(0);
  const [smsCampanhas, setSmsCampanhas] = useState<any[]>([]);
  const [smsStats, setSmsStats] = useState<any>(null);
  const [loadingSms, setLoadingSms] = useState(false);
  const [confCallForPapers, setConfCallForPapers] = useState(false);
  const [confScholarships, setConfScholarships] = useState(false);
  const [formData, setFormData] = useState<EventInput>({
    titulo: '',
    descricao: '',
    descricao_curta: '',
    local_nome: '',
    local_cidade: 'Praia',
    local_capacidade: undefined,
    data_inicio: '',
    data_fim: '',
    categoria: 'OTHER',
    tipo: 'PRESENCIAL',
    status: 'PUBLISHED',
    visibilidade: 'PUBLIC',
    idade_minima: 0,
    pass_ativo: false,
    cashless_ativo: false,
    nfc_ativo: false,
    gamificacao_ativa: false,
    chat_ativo: false,
    waitlist_ativa: false,
    beach_club_ativo: false,
    resort_ativo: false,
    documentos_ativo: false,
    sms_ativo: false,
    email_ativo: false,
    stock_ativo: false,
    flag_esgotado: false,
    organizador_nome: '',
    organizador_email: '',
    organizador_telefone: '',
    plano: 'STARTER',
  });
  const [entities, setEntities] = useState<{ id: string; desig: string }[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [planMenuAnchor, setPlanMenuAnchor] = useState<null | HTMLElement>(null);
  const [planMenuEvent, setPlanMenuEvent] = useState<Event | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [mapsLink, setMapsLink] = useState<string>('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [eventsResponse, statsResponse] = await Promise.all([
        eventsService.getEvents({
          page: page + 1,
          limit: rowsPerPage,
          ...(debouncedSearch && { search: debouncedSearch }),
          ...(statusFilter && { status: statusFilter }),
        }),
        eventsService.getStats().catch(() => null),
      ]);
      setEvents(eventsResponse.data || []);
      setTotalCount(eventsResponse.pagination?.total || eventsResponse.data?.length || 0);
      if (statsResponse) {
        setStats(statsResponse);
        if (statsResponse.needsSetup) {
          setNeedsSetup(true);
        }
      }
      if (eventsResponse.needsSetup) {
        setNeedsSetup(true);
      }
    } catch (err: any) {
      console.error('Error fetching events:', err);
      setError(err.message || 'Erro ao carregar eventos');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, statusFilter]);

  const handleSetup = async () => {
    setSetupLoading(true);
    setError(null);
    try {
      const result = await eventsService.setupTables();
      setNeedsSetup(false);
      setError(null);
      alert(`Tabelas criadas com sucesso: ${result.created?.join(', ') || 'Todas já existiam'}`);
      fetchEvents();
    } catch (err: any) {
      setError(err.message || 'Erro ao configurar tabelas');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleRunCrawler = async () => {
    if (!window.confirm('Correr o crawler de eventos públicos de Cabo Verde (agendacultural.cv, anacao.cv)?\nOs novos eventos entram como rascunho (DRAFT) para reveres antes de publicar.')) return;
    setCrawlerLoading(true);
    setError(null);
    try {
      const { stats } = await eventsService.runCrawler();
      alert(`Crawler concluído.\nEncontrados: ${stats.found}\nInseridos (DRAFT): ${stats.inserted}\nJá existiam: ${stats.skipped}\nErros: ${stats.errors}`);
      fetchEvents();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Erro ao correr o crawler.');
    } finally {
      setCrawlerLoading(false);
    }
  };

  const handleSeedEvents = async () => {
    if (!window.confirm('Semear a lista curada de eventos futuros reais de Cabo Verde (Out 2026 → 2027)?\nEntram como rascunho (DRAFT) para reveres antes de publicar. As datas anuais marcadas "(datas a confirmar)" devem ser revistas.')) return;
    setSeedLoading(true);
    setError(null);
    try {
      const { stats } = await eventsService.seedEvents();
      alert(`Seed concluído.\nEncontrados: ${stats.found}\nInseridos (DRAFT): ${stats.inserted}\nJá existiam: ${stats.skipped}\nErros: ${stats.errors}`);
      fetchEvents();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Erro ao semear os eventos.');
    } finally {
      setSeedLoading(false);
    }
  };

  const handleImportSocial = async () => {
    if (!importUrl.trim() && !importText.trim()) {
      setError('Cola um link do Facebook/Instagram ou o texto do post.');
      return;
    }
    setImportLoading(true);
    setError(null);
    setImportResult(null);
    try {
      const r = await eventsService.importSocial({ url: importUrl.trim() || undefined, text: importText.trim() || undefined });
      setImportResult({ eventId: r.eventId, warnings: r.warnings || [], message: r.message });
      fetchEvents();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Erro ao importar o evento.');
    } finally {
      setImportLoading(false);
    }
  };

  const closeImport = () => {
    if (importLoading) return;
    setImportOpen(false);
    setImportUrl('');
    setImportText('');
    setImportResult(null);
  };

  useEffect(() => {
    fetchEvents();
    entitiesService.getEntities({ limit: 100 }).then((res) => {
      setEntities((res.data || []).map((e: any) => ({ id: e.id, desig: e.desig || e.nomeContacto || '' })));
    }).catch(() => {});
  }, [fetchEvents]);

  const getStatusChip = (status?: string) => {
    const statusConfig = STATUS_OPTIONS.find(s => s.value === status);
    if (statusConfig) {
      return <Chip size="small" color={statusConfig.color} label={statusConfig.label} />;
    }
    return <Chip size="small" color="default" label={status || '-'} />;
  };

  const getPlanChip = (plano?: string) => {
    const p = PLANO_OPTIONS.find(o => o.value === plano) || PLANO_OPTIONS[0];
    return (
      <Chip
        size="small"
        icon={<PlanIcon style={{ fontSize: 13, color: p.color }} />}
        label={p.label}
        style={{ backgroundColor: p.bg, color: p.color, fontWeight: 600, fontSize: 11 }}
      />
    );
  };

  const getTipoChip = (tipo?: string) => {
    const tipoConfig = TIPOS.find(t => t.value === tipo);
    return <Chip size="small" variant="outlined" color="primary" label={tipoConfig?.label || tipo || '-'} />;
  };

  const getCategoriaLabel = (categoria?: string) => {
    const cat = CATEGORIAS.find(c => c.value === categoria);
    return cat?.label || categoria || '-';
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      // data_inicio é hora de parede de CV marcada com Z → mostrar em UTC
      return new Date(dateStr).toLocaleDateString('pt-CV', {
        timeZone: 'UTC',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // Converts a DB datetime string to a datetime-local input value (YYYY-MM-DDTHH:MM)
  // mysql2 appends .000Z to datetimes stored without timezone, but the stored value is already CVT.
  // Just strip the suffix — no arithmetic needed.
  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      const raw = dateStr.endsWith('.000Z') ? dateStr.slice(0, -5)
                : dateStr.endsWith('Z')     ? dateStr.slice(0, -1)
                : dateStr;
      return raw.slice(0, 16); // YYYY-MM-DDTHH:mm
    } catch {
      return '';
    }
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return `${Math.round(value || 0).toLocaleString('pt-PT')}$`;
  };

  const handleOpenEdit = (event?: Event) => {
    setDialogTabValue(0);
    setImageFile(null);
    if (event) {
      setSelectedEvent(event);
      setFormData({
        titulo: event.titulo || '',
        descricao: event.descricao || '',
        descricao_curta: event.descricao_curta || '',
        local_nome: event.local_nome || '',
        local_endereco: event.local_endereco || '',
        local_cidade: event.local_cidade || 'Praia',
        local_capacidade: event.local_capacidade,
        local_latitude: event.local_latitude ?? undefined,
        local_longitude: event.local_longitude ?? undefined,
        data_inicio: formatDateTime(event.data_inicio),
        data_fim: formatDateTime(event.data_fim),
        categoria: event.categoria || 'OTHER',
        tipo: event.tipo || 'PRESENCIAL',
        status: event.status || 'DRAFT',
        visibilidade: event.visibilidade || 'PUBLIC',
        idade_minima: event.idade_minima || 0,
        pass_ativo: event.pass_ativo || false,
        cashless_ativo: event.cashless_ativo || false,
        nfc_ativo: event.nfc_ativo || false,
        gamificacao_ativa: event.gamificacao_ativa || false,
        chat_ativo: event.chat_ativo || false,
        waitlist_ativa: event.waitlist_ativa || false,
        beach_club_ativo: event.beach_club_ativo || false,
        resort_ativo: event.resort_ativo || false,
        documentos_ativo: (event as any).documentos_ativo || false,
        sms_ativo: (event as any).sms_ativo || false,
        email_ativo: (event as any).email_ativo || false,
        stock_ativo: (event as any).stock_ativo || false,
        flag_esgotado: !!(event as any).flag_esgotado,
        organizador_nome: event.organizador_nome || '',
        organizador_email: event.organizador_email || '',
        organizador_telefone: event.organizador_telefone || '',
        entity_id: event.entity_id ? String(event.entity_id) : undefined,
        plano: event.plano || 'STARTER',
      });
      // Load existing image/video
      setImagePreview(event.imagem_url || event.imagem_capa || null);
      setVideoUrl(event.video_url || '');
      setMapsLink('');
      // Load conference config (non-blocking)
      setConfCallForPapers(false);
      setConfScholarships(false);
      api.get(`/conference/${event.id}/config`).then((r) => {
        const cfg = r.data?.config || r.data?.data?.config || {};
        setConfCallForPapers(cfg.callForPapersConfig?.enabled || false);
        setConfScholarships(cfg.scholarshipConfig?.enabled || false);
      }).catch(() => {});
    } else {
      setSelectedEvent(null);
      setConfCallForPapers(false);
      setConfScholarships(false);
      setFormData({
        titulo: '',
        descricao: '',
        descricao_curta: '',
        local_nome: '',
        local_endereco: '',
        local_cidade: 'Praia',
        local_capacidade: undefined,
        data_inicio: '',
        data_fim: '',
        categoria: 'OTHER',
        tipo: 'PRESENCIAL',
        status: 'PUBLISHED',
        visibilidade: 'PUBLIC',
        idade_minima: 0,
        cashless_ativo: false,
        nfc_ativo: false,
        gamificacao_ativa: false,
        chat_ativo: false,
        waitlist_ativa: false,
        beach_club_ativo: false,
        resort_ativo: false,
        documentos_ativo: false,
        sms_ativo: false,
        email_ativo: false,
        stock_ativo: false,
        flag_esgotado: false,
        organizador_nome: '',
        organizador_email: '',
        organizador_telefone: '',
        plano: 'STARTER',
      });
      setImagePreview(null);
      setVideoUrl('');
    }
    setDialogError(null);
    setEditDialog(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setDialogError(null);
    try {
      // Build form data with image
      const dataToSend: any = { ...formData, video_url: videoUrl };

      // If there's a new image file, convert to base64
      if (imageFile) {
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            // Remove data:image/xxx;base64, prefix
            const base64Data = result.split(',')[1];
            resolve(base64Data);
          };
          reader.readAsDataURL(imageFile);
        });
        dataToSend.imagem = base64;
        dataToSend.imagem_mime_type = imageFile.type;
      }

      if (selectedEvent) {
        await eventsService.updateEvent(selectedEvent.id, dataToSend);
        // Persist conference config toggles (non-blocking — creates config row if missing)
        if (confCallForPapers || confScholarships) {
          api.put(`/conference/${selectedEvent.id}/config`, {
            enabled: 1,
            callForPapersConfig: { enabled: confCallForPapers },
            scholarshipConfig: { enabled: confScholarships },
          }).catch(() => {});
        }
        setSnackbar({ open: true, message: 'Evento actualizado com sucesso', severity: 'success' });
      } else {
        const result = await eventsService.createEvent(dataToSend);
        const newId = result?.data?.id;
        if (newId && (confCallForPapers || confScholarships)) {
          api.put(`/conference/${newId}/config`, {
            enabled: 1,
            callForPapersConfig: { enabled: confCallForPapers },
            scholarshipConfig: { enabled: confScholarships },
          }).catch(() => {});
        }
        setSnackbar({ open: true, message: `Evento criado (ID: ${newId ?? '?'})`, severity: 'success' });
      }
      setEditDialog(false);
      setSelectedEvent(null);
      setImageFile(null);
      setImagePreview(null);
      setVideoUrl('');
      setDialogError(null);
      setPage(0);
      fetchEvents();
    } catch (err: any) {
      console.error('[handleSave] erro ao gravar evento:', err);
      setDialogError(err.message || 'Erro ao salvar evento');
      setSnackbar({ open: true, message: `Erro: ${err.message || 'falha desconhecida'}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (event: Event) => {
    if (!window.confirm(`Eliminar evento "${event.titulo}"?`)) {
      return;
    }

    try {
      setError(null);
      await eventsService.deleteEvent(event.id);
      fetchEvents();
    } catch (err: any) {
      setError(err.message || 'Erro ao eliminar evento');
    }
  };

  const handleApprove = async (event: Event) => {
    if (!window.confirm(`Aprovar e publicar evento "${event.titulo}"?`)) return;
    try {
      setError(null);
      await api.put(`/admin/events/${event.id}/approve`);
      fetchEvents();
    } catch (err: any) {
      setError(err.message || 'Erro ao aprovar evento');
    }
  };

  const handleReject = async (event: Event) => {
    const motivo = window.prompt(`Rejeitar "${event.titulo}"\n\nMotivo da rejeição (será enviado ao promotor):`);
    if (motivo === null) return; // cancelado
    try {
      setError(null);
      await api.put(`/admin/events/${event.id}/reject`, { motivo: motivo.trim() || undefined });
      fetchEvents();
    } catch (err: any) {
      setError(err.message || 'Erro ao rejeitar evento');
    }
  };

  const handlePlanMenuOpen = (e: React.MouseEvent<HTMLElement>, event: Event) => {
    setPlanMenuAnchor(e.currentTarget);
    setPlanMenuEvent(event);
  };

  const handlePlanMenuClose = () => {
    setPlanMenuAnchor(null);
    setPlanMenuEvent(null);
  };

  const handlePlanChange = async (newPlano: Plano) => {
    if (!planMenuEvent) return;
    handlePlanMenuClose();
    try {
      await eventsService.updateEventPlan(planMenuEvent.id, newPlano);
      fetchEvents();
      setSnackbar({ open: true, message: `Plano alterado para ${newPlano}`, severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Erro ao alterar plano', severity: 'error' });
    }
  };

  const handlePublish = async (event: Event) => {
    if (!window.confirm(`Publicar evento "${event.titulo}"?`)) {
      return;
    }

    try {
      setError(null);
      await eventsService.publishEvent(event.id);
      fetchEvents();
    } catch (err: any) {
      setError(err.message || 'Erro ao publicar evento');
    }
  };

  if (loading && events.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {needsSetup && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={setupLoading ? <CircularProgress size={16} /> : <SetupIcon />}
              onClick={handleSetup}
              disabled={setupLoading}
            >
              {setupLoading ? 'A configurar...' : 'Configurar Tabelas'}
            </Button>
          }
        >
          As tabelas de eventos ainda não foram configuradas. Clique em "Configurar Tabelas" para criar a estrutura necessária (incluindo Bilhetes, Cashless, Gamificação, Chat e mais).
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          Gestão de Eventos
        </Typography>
        {mainTab === 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchEvents}>
              Atualizar
            </Button>
            {isAdmin && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={crawlerLoading ? <CircularProgress size={16} /> : <CrawlerIcon />}
                onClick={handleRunCrawler}
                disabled={crawlerLoading}
              >
                {crawlerLoading ? 'A pesquisar…' : 'Correr crawler'}
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={seedLoading ? <CircularProgress size={16} /> : <CrawlerIcon />}
                onClick={handleSeedEvents}
                disabled={seedLoading}
              >
                {seedLoading ? 'A semear…' : 'Semear eventos'}
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CrawlerIcon />}
                onClick={() => setImportOpen(true)}
              >
                Importar de FB/IG
              </Button>
            )}
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
              Novo Evento
            </Button>
          </Box>
        )}
      </Box>

      {/* Main Navigation Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={mainTab}
          onChange={(_, v) => setMainTab(v)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<EventIcon />} label="Eventos" iconPosition="start" />
          <Tab icon={<TicketIcon />} label="Bilhetes" iconPosition="start" />
          <Tab icon={<NfcIcon />} label="Pulseiras NFC" iconPosition="start" />
          <Tab icon={<Storefront />} label="Vendedores" iconPosition="start" />
          <Tab icon={<PromoIcon />} label="Códigos Promo" iconPosition="start" />
          <Tab icon={<ImageIcon />} label="Hero Slides" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Lista de Eventos */}
      {mainTab === 0 && (
        <>
          {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats?.total_eventos || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Eventos</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats?.eventos_ativos || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Publicados</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ConfirmationNumber color="info" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats?.total_bilhetes_vendidos || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Bilhetes Vendidos</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney color="success" />
                <Box>
                  <Typography variant="h6" fontWeight={700}>{formatCurrency(stats?.receita_bilhetes)}</Typography>
                  <Typography variant="caption" color="text.secondary">Receita Bilhetes</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <People color="secondary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats?.total_checkins || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Check-ins</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney color="warning" />
                <Box>
                  <Typography variant="h6" fontWeight={700}>{formatCurrency(stats?.receita_cashless)}</Typography>
                  <Typography variant="caption" color="text.secondary">Receita Cashless</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search & Filters */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Pesquisar por nome, local, organização..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ flex: 1, minWidth: 260 }}
        />
        <TextField
          select
          size="small"
          label="Estado"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">Todos os estados</MenuItem>
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
          ))}
        </TextField>
        {(searchTerm || statusFilter) && (
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={() => { setSearchTerm(''); setStatusFilter(''); setPage(0); }}
          >
            Limpar filtros
          </Button>
        )}
      </Paper>

      {/* Events Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Evento</TableCell>
                <TableCell>Local / Data</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="center">Inscrições / Bilhetes</TableCell>
                <TableCell align="center">Funcionalidades</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Plano</TableCell>
                <TableCell width={180} align="center">Acções</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nenhum evento encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                events.map((event) => (
                  <TableRow key={event.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {event.titulo}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getCategoriaLabel(event.categoria)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <LocationOn fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {event.local_nome || event.local_cidade || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="body2">
                          {formatDate(event.data_inicio)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{getTipoChip(event.tipo)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={event.pass_ativo ? 'Bilhetes vendidos / capacidade' : 'Inscrições confirmadas / capacidade'}>
                        <Chip
                          size="small"
                          label={`${event.total_bilhetes_vendidos || 0}/${event.local_capacidade || '∞'}`}
                          color={event.pass_ativo ? 'info' : 'success'}
                        />
                      </Tooltip>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        {event.pass_ativo ? 'Bilhetes' : 'Inscrições'}
                      </Typography>
                      {event.pass_ativo && event.preco_minimo !== undefined && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {formatCurrency(event.preco_minimo)} - {formatCurrency(event.preco_maximo)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {event.cashless_ativo && (
                          <Tooltip title="Cashless Activo">
                            <NfcIcon fontSize="small" color="success" />
                          </Tooltip>
                        )}
                        {event.gamificacao_ativa && (
                          <Tooltip title="Gamificação">
                            <TrophyIcon fontSize="small" color="warning" />
                          </Tooltip>
                        )}
                        {event.chat_ativo && (
                          <Tooltip title="Chat">
                            <ChatIcon fontSize="small" color="info" />
                          </Tooltip>
                        )}
                        {!event.cashless_ativo && !event.gamificacao_ativa && !event.chat_ativo && (
                          <Typography variant="caption" color="text.disabled">-</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        {getStatusChip(event.status)}
                        {(event as any).flag_esgotado && (
                          <Chip size="small" color="warning" label="Esgotado" icon={<EsgotadoIcon />} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {getPlanChip(event.plano)}
                        {isAdmin && (
                          <Tooltip title="Alterar plano">
                            <IconButton size="small" onClick={(e) => handlePlanMenuOpen(e, event)} sx={{ p: 0.25 }}>
                              <DropdownIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver Detalhes">
                        <IconButton size="small" onClick={() => handleOpenEdit(event)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {event.status === 'PENDING_REVIEW' && (
                        <>
                          <Tooltip title="Aprovar">
                            <IconButton size="small" color="success" onClick={() => handleApprove(event)}>
                              <ApproveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Rejeitar">
                            <IconButton size="small" color="error" onClick={() => handleReject(event)}>
                              <RejectIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {event.status === 'DRAFT' && (
                        <Tooltip title="Publicar">
                          <IconButton size="small" color="success" onClick={() => handlePublish(event)}>
                            <PublishIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleOpenEdit(event)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => handleDelete(event)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>
        </>
      )}

      {/* Tab 1: Bilhetes */}
      {mainTab === 1 && (
        <TicketTypesManager events={events} />
      )}

      {/* Tab 2: Pulseiras NFC */}
      {mainTab === 2 && (
        <WristbandsManager events={events} />
      )}

      {/* Tab 3: Vendedores */}
      {mainTab === 3 && (
        <VendorsManager events={events} />
      )}

      {/* Tab 4: Códigos Promo */}
      {mainTab === 4 && (
        <PromoCodesManager events={events} />
      )}

      {/* Tab 5: Hero Slides */}
      {mainTab === 5 && (
        <HeroSlidesTab />
      )}

      {/* Edit/Create Dialog */}
      <Dialog
        open={editDialog}
        onClose={(_event, reason) => {
          // Prevent accidental close by clicking outside the dialog
          if (reason === 'backdropClick') return;
          setEditDialog(false);
          setDialogError(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventIcon />
          {selectedEvent ? 'Editar Evento' : 'Novo Evento'}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Tabs value={dialogTabValue} onChange={(_, v) => {
            setDialogTabValue(v);
            // Load SMS data when SMS tab is opened (tab 6)
            const smsTabIndex = isAdmin ? 6 : -1;
            if (v === smsTabIndex && selectedEvent && !loadingSms) {
              setLoadingSms(true);
              api.get(`/admin/events/${selectedEvent.id}/sms/reconciliacao`)
                .then(r => { setSmsCampanhas(r.data?.data?.campanhas || []); setSmsStats(r.data?.data?.totais || null); })
                .catch(() => {})
                .finally(() => setLoadingSms(false));
            }
          }} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Informações" />
            <Tab label="Local & Data" />
            <Tab icon={<ImageIcon />} label="Mídia" iconPosition="start" />
            <Tab label="Funcionalidades" />
            <Tab label="Organizador" />
            {isAdmin && <Tab icon={<PlanIcon />} label="Plano" iconPosition="start" />}
            {isAdmin && formData.sms_ativo && <Tab icon={<SmsIcon />} label="SMS" iconPosition="start" />}
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* Tab 0: Informações Básicas */}
            {dialogTabValue === 0 && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Título do Evento"
                    value={formData.titulo}
                    onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Descrição Curta"
                    value={formData.descricao_curta}
                    onChange={(e) => setFormData({ ...formData, descricao_curta: e.target.value })}
                    helperText="Até 500 caracteres"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Descrição Completa"
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    select
                    label="Categoria"
                    value={formData.categoria}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                  >
                    {CATEGORIAS.map((cat) => (
                      <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    select
                    label="Tipo"
                    value={formData.tipo}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  >
                    {TIPOS.map((tipo) => (
                      <MenuItem key={tipo.value} value={tipo.value}>{tipo.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    select
                    label="Estado"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!(formData as any).flag_esgotado}
                        onChange={(e) => setFormData({ ...formData, flag_esgotado: e.target.checked } as any)}
                        color="warning"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EsgotadoIcon sx={{ color: (formData as any).flag_esgotado ? 'warning.main' : 'text.secondary', fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>Esgotado</Typography>
                          <Typography variant="caption" color="text.secondary">
                            O evento permanece visível mas indica que não há bilhetes disponíveis
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Visibilidade"
                    value={formData.visibilidade}
                    onChange={(e) => setFormData({ ...formData, visibilidade: e.target.value })}
                  >
                    <MenuItem value="PUBLIC">Público</MenuItem>
                    <MenuItem value="PRIVATE">Privado</MenuItem>
                    <MenuItem value="UNLISTED">Não Listado</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Idade Mínima"
                    value={formData.idade_minima || ''}
                    onChange={(e) => setFormData({ ...formData, idade_minima: parseInt(e.target.value) || 0 })}
                  />
                </Grid>
              </Grid>
            )}

            {/* Tab 1: Local e Data */}
            {dialogTabValue === 1 && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Nome do Local"
                    value={formData.local_nome}
                    onChange={(e) => setFormData({ ...formData, local_nome: e.target.value })}
                    placeholder="Ex: Auditório Nacional"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Cidade"
                    value={formData.local_cidade}
                    onChange={(e) => setFormData({ ...formData, local_cidade: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Endereço"
                    value={formData.local_endereco}
                    onChange={(e) => setFormData({ ...formData, local_endereco: e.target.value })}
                    placeholder="Endereço completo"
                  />
                </Grid>

                {/* Google Maps link parser */}
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      fullWidth
                      label="Link do Google Maps (para extrair coordenadas)"
                      value={mapsLink}
                      onChange={(e) => setMapsLink(e.target.value)}
                      placeholder="Cola aqui o link do Google Maps do local exato"
                      size="small"
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
                      onClick={() => {
                        // Try multiple URL formats
                        let lat: number | null = null;
                        let lng: number | null = null;
                        // Format: /@lat,lng or @lat,lng
                        let m = mapsLink.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
                        if (m) { lat = parseFloat(m[1]); lng = parseFloat(m[2]); }
                        // Format: !3dlat!4dlng
                        if (!lat) { m = mapsLink.match(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/); if (m) { lat = parseFloat(m[1]); lng = parseFloat(m[2]); } }
                        // Format: q=lat,lng
                        if (!lat) { m = mapsLink.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/); if (m) { lat = parseFloat(m[1]); lng = parseFloat(m[2]); } }
                        // Format: ll=lat,lng
                        if (!lat) { m = mapsLink.match(/ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/); if (m) { lat = parseFloat(m[1]); lng = parseFloat(m[2]); } }
                        if (lat && lng) {
                          setFormData({ ...formData, local_latitude: lat, local_longitude: lng });
                          setMapsLink('');
                        } else {
                          alert('Não foi possível extrair coordenadas deste link. Tenta copiar o link directo do Google Maps (botão Partilhar → Copiar link).');
                        }
                      }}
                    >
                      Extrair coordenadas
                    </Button>
                  </Box>
                </Grid>

                {/* Lat/Lng fields + map preview */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Latitude"
                    value={formData.local_latitude ?? ''}
                    onChange={(e) => setFormData({ ...formData, local_latitude: parseFloat(e.target.value) || undefined })}
                    placeholder="Ex: 14.9340"
                    inputProps={{ step: 'any' }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Longitude"
                    value={formData.local_longitude ?? ''}
                    onChange={(e) => setFormData({ ...formData, local_longitude: parseFloat(e.target.value) || undefined })}
                    placeholder="Ex: -23.5087"
                    inputProps={{ step: 'any' }}
                  />
                </Grid>
                {formData.local_latitude && formData.local_longitude && (
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ borderRadius: 2, overflow: 'hidden', height: 200 }}>
                      <iframe
                        title="Pré-visualização do mapa"
                        src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyD35pZFnJYKEXKJ_XIC8aVG7OkeQNmJpn0&q=${formData.local_latitude},${formData.local_longitude}&zoom=16&language=pt`}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        allowFullScreen
                        loading="lazy"
                      />
                    </Box>
                  </Grid>
                )}

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Capacidade"
                    value={formData.local_capacidade || ''}
                    onChange={(e) => setFormData({ ...formData, local_capacidade: parseInt(e.target.value) || undefined })}
                    placeholder="Máximo de participantes"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }}>Data e Hora</Divider>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Data/Hora de Início"
                    value={formData.data_inicio}
                    onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Data/Hora de Fim"
                    value={formData.data_fim}
                    onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            )}

            {/* Tab 2: Mídia */}
            {dialogTabValue === 2 && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Imagem do Evento (Banner/Cartaz)
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: 'background.default',
                      position: 'relative',
                    }}
                  >
                    {imagePreview ? (
                      <Box sx={{ position: 'relative' }}>
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: 300,
                            objectFit: 'contain',
                            borderRadius: 8,
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={handleRemoveImage}
                          sx={{
                            position: 'absolute',
                            top: -10,
                            right: -10,
                            bgcolor: 'error.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'error.dark' },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box>
                        <ImageIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Arraste uma imagem ou clique para selecionar
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          Formatos suportados: JPG, PNG, GIF (máx. 5MB)
                        </Typography>
                      </Box>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                      }}
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }}>Vídeo Promocional</Divider>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="URL do Vídeo (YouTube, Vimeo)"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    InputProps={{
                      startAdornment: <VideoIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                    helperText="Cole o link do YouTube ou Vimeo para adicionar um vídeo promocional"
                  />
                </Grid>
                {videoUrl && (() => {
                  const m = videoUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
                  const videoId = m?.[1];
                  return videoId ? (
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ position: 'relative', width: '100%', paddingBottom: '56.25%', borderRadius: 2, overflow: 'hidden', bgcolor: 'black' }}>
                        <iframe
                          src={`https://www.youtube.com/embed/${videoId}`}
                          title="Preview do vídeo"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
                        />
                      </Box>
                    </Grid>
                  ) : (
                    <Grid size={{ xs: 12 }}>
                      <Alert severity="warning">URL de vídeo inválido. Use um link do YouTube.</Alert>
                    </Grid>
                  );
                })()}
              </Grid>
            )}

            {/* Tab 3: Funcionalidades */}
            {dialogTabValue === 3 && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Ative as funcionalidades que deseja para este evento:
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Card
                    variant="outlined"
                    sx={{ borderColor: formData.pass_ativo ? 'success.main' : 'divider', borderWidth: formData.pass_ativo ? 2 : 1 }}
                  >
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.pass_ativo || false}
                            onChange={(e) => setFormData({ ...formData, pass_ativo: e.target.checked })}
                            color="success"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TicketIcon sx={{ color: formData.pass_ativo ? 'success.main' : 'text.secondary' }} />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                Pass (Bilhetes)
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formData.pass_ativo
                                  ? 'Criação de bilhetes pagos activa — tipos de bilhete com preço > 0 permitidos'
                                  : 'Desactivado — evento só permite Free Pass (bilhetes gratuitos)'}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.cashless_ativo}
                            onChange={(e) => setFormData({ ...formData, cashless_ativo: e.target.checked })}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <NfcIcon color="primary" />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Cashless / NFC</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Pagamentos por pulseira NFC
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.nfc_ativo}
                            onChange={(e) => setFormData({ ...formData, nfc_ativo: e.target.checked })}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <QrIcon color="primary" />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Check-in NFC</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Check-in por pulseira NFC
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.gamificacao_ativa}
                            onChange={(e) => setFormData({ ...formData, gamificacao_ativa: e.target.checked })}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrophyIcon color="warning" />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Gamificação</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Badges, desafios, leaderboard
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.chat_ativo}
                            onChange={(e) => setFormData({ ...formData, chat_ativo: e.target.checked })}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ChatIcon color="info" />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Chat do Evento</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Comunicação entre participantes
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.waitlist_ativa}
                            onChange={(e) => setFormData({ ...formData, waitlist_ativa: e.target.checked })}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <People color="secondary" />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Lista de Espera</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Waitlist quando esgotado
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.beach_club_ativo}
                            onChange={(e) => setFormData({ ...formData, beach_club_ativo: e.target.checked })}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BeachAccessIcon color="info" />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Beach Club</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Zonas, sunbeds e reservas
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.resort_ativo}
                            onChange={(e) => setFormData({ ...formData, resort_ativo: e.target.checked })}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <HotelIcon color="action" />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Resort / Multi-Day</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Quartos, amenidades e hóspedes
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={confCallForPapers}
                            onChange={(e) => setConfCallForPapers(e.target.checked)}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PaperIcon color="primary" />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Call for Papers</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Submissão de trabalhos científicos
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={confScholarships}
                            onChange={(e) => setConfScholarships(e.target.checked)}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ScholarshipIcon sx={{ color: 'success.main' }} />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Bolsas</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Candidaturas a bolsas de participação
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.documentos_ativo}
                            onChange={(e) => setFormData({ ...formData, documentos_ativo: e.target.checked })}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FolderOpenIcon sx={{ color: 'text.secondary' }} />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Documentos</Typography>
                              <Typography variant="caption" color="text.secondary">
                                PDFs, regulamentos e ficheiros do evento
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Stock */}
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card variant="outlined" sx={{ borderColor: (formData as any).stock_ativo ? 'secondary.main' : 'divider', bgcolor: (formData as any).stock_ativo ? 'secondary.50' : undefined }}>
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!(formData as any).stock_ativo}
                            onChange={(e) => setFormData({ ...formData, stock_ativo: e.target.checked } as any)}
                            color="secondary"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <StockIcon sx={{ color: 'text.secondary' }} />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Gestão de Stock</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Distribuição de passes por canais de venda (online, lojas, PDV)
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* SMS */}
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card variant="outlined" sx={{ borderColor: formData.sms_ativo ? 'warning.main' : 'divider', bgcolor: formData.sms_ativo ? 'warning.50' : undefined }}>
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!formData.sms_ativo}
                            onChange={(e) => setFormData({ ...formData, sms_ativo: e.target.checked })}
                            color="warning"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SmsIcon sx={{ color: 'text.secondary' }} />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>SMS</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Envio de SMS para compradores (12 CVE/SMS)
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Email */}
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card variant="outlined" sx={{ borderColor: (formData as any).email_ativo ? 'warning.main' : 'divider', bgcolor: (formData as any).email_ativo ? 'warning.50' : undefined }}>
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!(formData as any).email_ativo}
                            onChange={(e) => setFormData({ ...formData, email_ativo: e.target.checked } as any)}
                            color="warning"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MailIcon sx={{ color: 'text.secondary' }} />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Email</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Envio de email em massa aos compradores (sem custo)
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Esgotado */}
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card variant="outlined" sx={{ borderColor: (formData as any).flag_esgotado ? 'warning.main' : 'divider', borderWidth: (formData as any).flag_esgotado ? 2 : 1 }}>
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!(formData as any).flag_esgotado}
                            onChange={(e) => setFormData({ ...formData, flag_esgotado: e.target.checked } as any)}
                            color="warning"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EsgotadoIcon sx={{ color: (formData as any).flag_esgotado ? 'warning.main' : 'text.secondary' }} />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Esgotado</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Bloqueia compras — o evento fica visível mas sem bilhetes disponíveis
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>

              </Grid>
            )}

            {/* Tab 4: Organizador */}
            {dialogTabValue === 4 && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Informações do organizador (visível publicamente):
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    select
                    label="Entidade"
                    value={formData.entity_id || ''}
                    onChange={(e) => { const v = e.target.value; setFormData({ ...formData, entity_id: v || undefined }); }}
                    helperText="Selecione a entidade associada a este evento"
                  >
                    <MenuItem value="">— Nenhuma —</MenuItem>
                    {entities.map((ent) => (
                      <MenuItem key={ent.id} value={ent.id}>{ent.desig}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Nome do Organizador"
                    value={formData.organizador_nome}
                    onChange={(e) => setFormData({ ...formData, organizador_nome: e.target.value })}
                    placeholder="Nome da empresa ou pessoa"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Email do Organizador"
                    value={formData.organizador_email}
                    onChange={(e) => setFormData({ ...formData, organizador_email: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Telefone do Organizador"
                    value={formData.organizador_telefone}
                    onChange={(e) => setFormData({ ...formData, organizador_telefone: e.target.value })}
                  />
                </Grid>
              </Grid>
            )}

            {/* Tab 5: Plano (admin only) */}
            {isAdmin && dialogTabValue === 5 && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Plano tarifário do evento:
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    O plano determina quais funcionalidades estão disponíveis. Apenas administradores podem alterar o plano.
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ mb: 1, p: 1.5, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                    <Typography variant="caption" color="info.dark">
                      O plano é herdado automaticamente da entidade promotora ao criar o evento. Aqui pode fazer override para este evento específico.
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    select
                    label="Plano"
                    value={formData.plano || 'STARTER'}
                    onChange={(e) => setFormData({ ...formData, plano: e.target.value })}
                  >
                    {PLANO_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <PlanIcon style={{ color: opt.color, fontSize: 18 }} />
                          <Box>
                            <Typography variant="body2" fontWeight={600} style={{ color: opt.color }}>
                              {opt.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {opt.description}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {PLANO_OPTIONS.map((opt) => (
                  <Grid key={opt.value} size={{ xs: 12, md: 4 }}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderColor: formData.plano === opt.value ? opt.color : 'divider',
                        borderWidth: formData.plano === opt.value ? 2 : 1,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                      onClick={() => setFormData({ ...formData, plano: opt.value })}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <PlanIcon style={{ color: opt.color }} />
                          <Typography variant="subtitle2" fontWeight={700} style={{ color: opt.color }}>
                            {opt.label}
                          </Typography>
                        </Box>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                          {opt.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
            {/* Tab 6: SMS (admin only, sms_ativo) */}
            {isAdmin && dialogTabValue === 6 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <SmsIcon color="warning" />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>Campanhas SMS</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Histórico de envios e custos para reconciliação com o promotor
                    </Typography>
                  </Box>
                </Box>

                {/* Totals */}
                {smsStats && (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {[
                      { label: 'Campanhas', value: smsStats.num_campanhas },
                      { label: 'SMS Enviados', value: Number(smsStats.sms_enviados || 0).toLocaleString() },
                      { label: 'Custo Total', value: `${Number(smsStats.custo_total_cve || 0).toLocaleString()} CVE` },
                    ].map(s => (
                      <Grid key={s.label} size={{ xs: 4 }}>
                        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                          <Typography variant="h6" fontWeight={700}>{s.value}</Typography>
                          <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {loadingSms ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                ) : smsCampanhas.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                    Sem campanhas SMS para este evento.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell>Campanha</TableCell>
                          <TableCell align="center">Enviados</TableCell>
                          <TableCell align="center">Falhados</TableCell>
                          <TableCell align="right">Custo (CVE)</TableCell>
                          <TableCell align="center">Estado</TableCell>
                          <TableCell align="right">Data</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {smsCampanhas.map((c: any) => (
                          <TableRow key={c.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>{c.nome}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {c.mensagem}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={c.enviados} size="small" color="success" variant="outlined" sx={{ minWidth: 40 }} />
                            </TableCell>
                            <TableCell align="center">
                              {c.falhados > 0 ? <Chip label={c.falhados} size="small" color="error" variant="outlined" sx={{ minWidth: 40 }} /> : '—'}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600}>
                                {Number(c.custo_total || 0).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={c.status === 'CONCLUIDO' ? 'Concluído' : c.status === 'ENVIANDO' ? 'A enviar' : 'Falhado'}
                                size="small"
                                color={c.status === 'CONCLUIDO' ? 'success' : c.status === 'ENVIANDO' ? 'info' : 'error'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" color="text.secondary">
                                {new Date(c.criado_em).toLocaleDateString('pt-CV', { timeZone: CV_TZ, day: '2-digit', month: 'short', year: 'numeric' })}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                  <Typography variant="caption" color="warning.dark">
                    Os custos SMS são cobrados ao promotor na reconciliação mensal. Custo unitário: 12 CVE / segmento SMS.
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        {dialogError && (
          <Box sx={{ px: 3, pb: 1 }}>
            <Alert severity="error" onClose={() => setDialogError(null)}>
              {dialogError}
            </Alert>
          </Box>
        )}
        <DialogActions>
          <Button onClick={() => { setEditDialog(false); setDialogError(null); }} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.titulo || !formData.data_inicio}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving ? 'A guardar...' : (selectedEvent ? 'Guardar' : 'Criar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Plan upgrade quick menu */}
      <Menu
        anchorEl={planMenuAnchor}
        open={Boolean(planMenuAnchor)}
        onClose={handlePlanMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {PLANO_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.value}
            selected={planMenuEvent?.plano === opt.value}
            onClick={() => handlePlanChange(opt.value)}
            sx={{ minWidth: 220 }}
          >
            <ListItemIcon>
              <PlanIcon style={{ color: opt.color }} fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={<span style={{ fontWeight: 600, color: opt.color }}>{opt.label}</span>}
              secondary={opt.description}
            />
          </MenuItem>
        ))}
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Importar de Facebook / Instagram — colar & parsear */}
      <Dialog open={importOpen} onClose={closeImport} maxWidth="sm" fullWidth>
        <DialogTitle>Importar evento de Facebook / Instagram</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cola o link público do evento/post <strong>ou</strong> o texto da publicação.
            Extraio título, data, cidade e imagem e crio um <strong>rascunho</strong> para reveres.
            Não faço login nem acedo a conteúdo privado — se o link for privado, cola o texto do post.
          </Typography>
          <TextField
            fullWidth size="small" label="Link do Facebook/Instagram (opcional)"
            placeholder="https://www.instagram.com/p/…  ou  https://www.facebook.com/events/…"
            value={importUrl} onChange={(e) => setImportUrl(e.target.value)}
            sx={{ mb: 2 }} disabled={importLoading}
          />
          <TextField
            fullWidth size="small" label="Texto do post (recomendado)" multiline minRows={4}
            placeholder="Cola aqui a legenda do post: nome do evento, data (ex.: 15 de junho 2026), local…"
            value={importText} onChange={(e) => setImportText(e.target.value)}
            disabled={importLoading}
          />
          {importResult && (
            <Alert severity={importResult.warnings.length ? 'warning' : 'success'} sx={{ mt: 2 }}>
              {importResult.message}
              {importResult.warnings.length > 0 && (
                <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                  {importResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeImport} disabled={importLoading}>
            {importResult ? 'Fechar' : 'Cancelar'}
          </Button>
          <Button
            variant="contained"
            onClick={handleImportSocial}
            disabled={importLoading || (!importUrl.trim() && !importText.trim())}
            startIcon={importLoading ? <CircularProgress size={16} /> : <CrawlerIcon />}
          >
            {importLoading ? 'A importar…' : 'Importar rascunho'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ============================================
// TICKET TYPES MANAGER COMPONENT
// ============================================

interface TicketTypesManagerProps {
  events: Event[];
}

const TicketTypesManager: React.FC<TicketTypesManagerProps> = ({ events: _eventsProp }) => {
  // Fetch all events independently so the dropdown is not limited by pagination
  const [allEvents, setAllEvents] = useState<Event[]>(_eventsProp);
  useEffect(() => {
    eventsService.getEvents({ page: 1, limit: 500 }).then((r: any) => {
      if (r.data?.length) setAllEvents(r.data);
    }).catch(() => {});
  }, []);

  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [ticketTypes, setTicketTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    nome: '',
    descricao: '',
    preco: '',
    preco_original: '',
    quantidade_total: '',
    venda_inicio: '',
    venda_fim: '',
    min_por_compra: '1',
    max_por_compra: '10',
    zona_acesso: '',
    beneficios: '',
    transferivel: true,
    reembolsavel: true,
  });

  const publishedEvents = allEvents.filter((e) => e.status === 'PUBLISHED' || e.status === 'DRAFT');

  const loadTicketTypes = useCallback(async (eventId: number) => {
    setLoading(true);
    try {
      const result = await eventsService.getTicketTypes(eventId);
      setTicketTypes(result.data || []);
    } catch (err) {
      console.error('Error loading ticket types:', err);
      setTicketTypes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      loadTicketTypes(selectedEventId);
    }
  }, [selectedEventId, loadTicketTypes]);

  const resetForm = () => {
    setForm({
      nome: '', descricao: '', preco: '', preco_original: '',
      quantidade_total: '', venda_inicio: '', venda_fim: '',
      min_por_compra: '1', max_por_compra: '10', zona_acesso: '',
      beneficios: '', transferivel: true, reembolsavel: true,
    });
    setEditingType(null);
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (tt: any) => {
    setEditingType(tt);
    setForm({
      nome: tt.nome || '',
      descricao: tt.descricao || '',
      preco: String(tt.preco || ''),
      preco_original: String(tt.preco_original || ''),
      quantidade_total: String(tt.quantidade_total || ''),
      venda_inicio: tt.venda_inicio ? tt.venda_inicio.slice(0, 16) : '',
      venda_fim: tt.venda_fim ? tt.venda_fim.slice(0, 16) : '',
      min_por_compra: String(tt.min_por_compra || 1),
      max_por_compra: String(tt.max_por_compra || 10),
      zona_acesso: tt.zona_acesso || '',
      beneficios: (tt.beneficios || []).join(', '),
      transferivel: tt.transferivel !== false,
      reembolsavel: tt.reembolsavel !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedEventId || !form.nome || !form.preco || !form.quantidade_total) return;
    setSaving(true);
    try {
      const data = {
        nome: form.nome,
        descricao: form.descricao || undefined,
        preco: parseFloat(form.preco),
        preco_original: form.preco_original ? parseFloat(form.preco_original) : undefined,
        quantidade_total: parseInt(form.quantidade_total),
        venda_inicio: form.venda_inicio || undefined,
        venda_fim: form.venda_fim || undefined,
        min_por_compra: parseInt(form.min_por_compra) || 1,
        max_por_compra: parseInt(form.max_por_compra) || 10,
        zona_acesso: form.zona_acesso || undefined,
        beneficios: form.beneficios ? form.beneficios.split(',').map((b: string) => b.trim()).filter(Boolean) : undefined,
        transferivel: form.transferivel,
        reembolsavel: form.reembolsavel,
      };

      if (editingType) {
        await eventsService.updateTicketType(selectedEventId, editingType.id, data);
      } else {
        await eventsService.createTicketType(selectedEventId, data);
      }
      setDialogOpen(false);
      resetForm();
      loadTicketTypes(selectedEventId);
    } catch (err) {
      console.error('Error saving ticket type:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (typeId: number) => {
    if (!selectedEventId || !window.confirm('Eliminar este tipo de bilhete?')) return;
    try {
      await eventsService.deleteTicketType(selectedEventId, typeId);
      loadTicketTypes(selectedEventId);
    } catch (err) {
      console.error('Error deleting ticket type:', err);
    }
  };

  const selectedEvent = publishedEvents.find((e) => e.id === selectedEventId);

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>Gestão de Bilhetes</Typography>
        {selectedEventId && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Novo Tipo de Bilhete
          </Button>
        )}
      </Box>

      {/* Event Selector */}
      <TextField
        select
        fullWidth
        label="Selecionar Evento"
        value={selectedEventId || ''}
        onChange={(e) => setSelectedEventId(Number(e.target.value))}
        sx={{ mb: 3 }}
      >
        <MenuItem value="">— Selecionar evento —</MenuItem>
        {publishedEvents.map((evt) => (
          <MenuItem key={evt.id} value={evt.id}>
            {evt.titulo} ({evt.status})
          </MenuItem>
        ))}
      </TextField>

      {!selectedEventId && (
        <Alert severity="info">Selecione um evento acima para gerir os tipos de bilhete.</Alert>
      )}

      {selectedEventId && loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {selectedEventId && !loading && (
        <>
          {/* Event Summary */}
          {selectedEvent && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>{selectedEvent.titulo}</strong> — {selectedEvent.local_cidade || 'Local não definido'}
              {selectedEvent.data_inicio && ` | ${new Date(selectedEvent.data_inicio).toLocaleDateString('pt-CV', { timeZone: 'UTC' })}`}
            </Alert>
          )}

          {/* Ticket Types Table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell><strong>Nome</strong></TableCell>
                  <TableCell align="right"><strong>Preço (CVE)</strong></TableCell>
                  <TableCell align="center"><strong>Total</strong></TableCell>
                  <TableCell align="center"><strong>Vendidos</strong></TableCell>
                  <TableCell align="center"><strong>Disponíveis</strong></TableCell>
                  <TableCell align="center"><strong>Estado</strong></TableCell>
                  <TableCell align="center"><strong>Acções</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ticketTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        Nenhum tipo de bilhete. Clique em "Novo Tipo de Bilhete" para criar.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  ticketTypes.map((tt: any) => {
                    const disponivel = (tt.quantidade_total || 0) - (tt.quantidade_vendida || 0) - (tt.quantidade_reservada || 0);
                    return (
                      <TableRow key={tt.id} hover>
                        <TableCell>
                          <Typography fontWeight={600} fontSize={14}>{tt.nome}</Typography>
                          {tt.descricao && <Typography variant="caption" color="text.secondary">{tt.descricao}</Typography>}
                          {tt.zona_acesso && <Chip label={tt.zona_acesso} size="small" sx={{ ml: 1 }} />}
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600}>
                            {tt.preco === 0 ? 'Grátis' : `${Number(tt.preco).toLocaleString('pt-CV')} CVE`}
                          </Typography>
                          {tt.preco_original && tt.preco_original > tt.preco && (
                            <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                              {Number(tt.preco_original).toLocaleString('pt-CV')} CVE
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">{tt.quantidade_total}</TableCell>
                        <TableCell align="center">{tt.quantidade_vendida || 0}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={disponivel}
                            size="small"
                            color={disponivel > 10 ? 'success' : disponivel > 0 ? 'warning' : 'error'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={tt.status || 'ACTIVE'}
                            size="small"
                            color={tt.status === 'ACTIVE' ? 'success' : tt.status === 'SOLD_OUT' ? 'error' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => openEdit(tt)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton size="small" color="error" onClick={() => handleDelete(tt.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingType ? 'Editar Tipo de Bilhete' : 'Novo Tipo de Bilhete'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nome *"
              fullWidth
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              placeholder="Ex: VIP, Normal, Estudante"
            />
            <TextField
              label="Descrição"
              fullWidth
              multiline
              rows={2}
              value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Preço (CVE) *"
                type="number"
                fullWidth
                value={form.preco}
                onChange={(e) => setForm({ ...form, preco: e.target.value })}
                placeholder="0 = Grátis"
              />
              <TextField
                label="Preço Original"
                type="number"
                fullWidth
                value={form.preco_original}
                onChange={(e) => setForm({ ...form, preco_original: e.target.value })}
                helperText="Riscado (promoção)"
              />
            </Box>
            <TextField
              label="Quantidade Total *"
              type="number"
              fullWidth
              value={form.quantidade_total}
              onChange={(e) => setForm({ ...form, quantidade_total: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Mín. por Compra"
                type="number"
                fullWidth
                value={form.min_por_compra}
                onChange={(e) => setForm({ ...form, min_por_compra: e.target.value })}
              />
              <TextField
                label="Máx. por Compra"
                type="number"
                fullWidth
                value={form.max_por_compra}
                onChange={(e) => setForm({ ...form, max_por_compra: e.target.value })}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Início Vendas"
                type="datetime-local"
                fullWidth
                value={form.venda_inicio}
                onChange={(e) => setForm({ ...form, venda_inicio: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                label="Fim Vendas"
                type="datetime-local"
                fullWidth
                value={form.venda_fim}
                onChange={(e) => setForm({ ...form, venda_fim: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Box>
            <TextField
              label="Zona de Acesso"
              fullWidth
              value={form.zona_acesso}
              onChange={(e) => setForm({ ...form, zona_acesso: e.target.value })}
              placeholder="Ex: Pista, Camarote, Backstage"
            />
            <TextField
              label="Benefícios (separados por vírgula)"
              fullWidth
              value={form.beneficios}
              onChange={(e) => setForm({ ...form, beneficios: e.target.value })}
              placeholder="Ex: Welcome drink, Acesso VIP, Parking"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={<Switch checked={form.transferivel} onChange={(e) => setForm({ ...form, transferivel: e.target.checked })} />}
                label="Transferível"
              />
              <FormControlLabel
                control={<Switch checked={form.reembolsavel} onChange={(e) => setForm({ ...form, reembolsavel: e.target.checked })} />}
                label="Reembolsável"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.nome || !form.preco || !form.quantidade_total}
          >
            {saving ? <CircularProgress size={20} /> : editingType ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// ============================================
// VENDORS MANAGER
// ============================================

const VENDOR_CATEGORIES = [
  { value: 'FOOD', label: 'Alimentação' },
  { value: 'DRINKS', label: 'Bebidas' },
  { value: 'MERCHANDISE', label: 'Merchandise' },
  { value: 'SERVICES', label: 'Serviços' },
  { value: 'OTHER', label: 'Outro' },
];

const VENDOR_STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pendente', color: 'warning' as const },
  { value: 'APPROVED', label: 'Aprovado', color: 'info' as const },
  { value: 'ACTIVE', label: 'Activo', color: 'success' as const },
  { value: 'SUSPENDED', label: 'Suspenso', color: 'error' as const },
  { value: 'INACTIVE', label: 'Inactivo', color: 'default' as const },
];

// ============================================
// WRISTBANDS MANAGER COMPONENT
// ============================================

const STATUS_COLORS: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary'> = {
  AVAILABLE: 'success',
  ASSIGNED: 'info',
  ACTIVE: 'primary',
  BLOCKED: 'error',
  LOST: 'warning',
  RETURNED: 'default',
};

interface WristbandsManagerProps {
  events: Event[];
}

const WristbandsManager: React.FC<WristbandsManagerProps> = ({ events: _eventsProp }) => {
  const [allEvents, setAllEvents] = useState<Event[]>(_eventsProp);
  useEffect(() => {
    eventsService.getEvents({ page: 1, limit: 500 }).then((r: any) => {
      if (r.data?.length) setAllEvents(r.data);
    }).catch(() => {});
  }, []);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [wristbands, setWristbands] = useState<Wristband[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [singleDialogOpen, setSingleDialogOpen] = useState(false);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [singleForm, setSingleForm] = useState({ nfc_uid: '', nfc_serial: '' });
  const [batchText, setBatchText] = useState('');
  const [batchResult, setBatchResult] = useState<any>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');

  const availableEvents = allEvents.filter((e) => e.status === 'PUBLISHED' || e.status === 'DRAFT');

  const loadWristbands = useCallback(async (eventId: number) => {
    setLoading(true);
    try {
      const result = await eventsService.getWristbands(eventId, {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      setWristbands(result.data || []);
      setTotal(result.pagination?.total || 0);
    } catch (err) {
      console.error('Error loading wristbands:', err);
      setWristbands([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  const loadStats = useCallback(async (eventId: number) => {
    try {
      const result = await eventsService.getWristbandStats(eventId);
      setStats(result.data || null);
    } catch (err) {
      console.error('Error loading wristband stats:', err);
    }
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      loadWristbands(selectedEventId);
      loadStats(selectedEventId);
    }
  }, [selectedEventId, loadWristbands, loadStats]);

  const handleCreateSingle = async () => {
    if (!selectedEventId || !singleForm.nfc_uid) return;
    setSaving(true);
    try {
      await eventsService.createWristband(selectedEventId, {
        nfc_uid: singleForm.nfc_uid.trim().toUpperCase(),
        nfc_serial: singleForm.nfc_serial || undefined,
      });
      setSingleDialogOpen(false);
      setSingleForm({ nfc_uid: '', nfc_serial: '' });
      loadWristbands(selectedEventId);
      loadStats(selectedEventId);
    } catch (err: any) {
      console.error('Error creating wristband:', err);
      alert(err?.response?.data?.error || 'Erro ao criar pulseira');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateBatch = async () => {
    if (!selectedEventId || !batchText.trim()) return;
    setSaving(true);
    setBatchResult(null);
    try {
      const lines = batchText.split('\n').map(l => l.trim()).filter(Boolean);
      const wristbandsData = lines.map(line => {
        const parts = line.split(/[,;\t]/).map(p => p.trim());
        return { nfc_uid: parts[0], nfc_serial: parts[1] || undefined };
      });
      const result = await eventsService.createWristbandBatch(selectedEventId, wristbandsData);
      setBatchResult(result.data || result);
      loadWristbands(selectedEventId);
      loadStats(selectedEventId);
    } catch (err: any) {
      console.error('Error batch creating wristbands:', err);
      alert(err?.response?.data?.error || 'Erro ao importar lote');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (wristbandId: number) => {
    if (!selectedEventId) return;
    if (!window.confirm('Remover esta pulseira do stock?')) return;
    try {
      await eventsService.deleteWristband(selectedEventId, wristbandId);
      loadWristbands(selectedEventId);
      loadStats(selectedEventId);
    } catch (err: any) {
      console.error('Error deleting wristband:', err);
      alert(err?.response?.data?.error || 'Erro ao remover pulseira');
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <NfcIcon color="primary" />
          <Typography variant="h6">Gestão de Pulseiras NFC</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            select
            label="Evento"
            value={selectedEventId || ''}
            onChange={(e) => {
              setSelectedEventId(Number(e.target.value));
              setPage(0);
            }}
            sx={{ minWidth: 250 }}
            size="small"
          >
            {availableEvents.map((ev) => (
              <MenuItem key={ev.id} value={ev.id}>{ev.titulo}</MenuItem>
            ))}
          </TextField>
          {selectedEventId && (
            <>
              <TextField
                select
                label="Estado"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                sx={{ minWidth: 140 }}
                size="small"
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="AVAILABLE">Disponível</MenuItem>
                <MenuItem value="ASSIGNED">Atribuída</MenuItem>
                <MenuItem value="ACTIVE">Ativa</MenuItem>
                <MenuItem value="BLOCKED">Bloqueada</MenuItem>
                <MenuItem value="LOST">Perdida</MenuItem>
                <MenuItem value="RETURNED">Devolvida</MenuItem>
              </TextField>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setSingleDialogOpen(true)}>
                Registar Pulseira
              </Button>
              <Button variant="contained" startIcon={<UploadIcon />} onClick={() => { setBatchDialogOpen(true); setBatchResult(null); setBatchText(''); }}>
                Importar Lote
              </Button>
            </>
          )}
        </Box>
      </Box>

      {!selectedEventId ? (
        <Alert severity="info">Selecione um evento para gerir pulseiras NFC</Alert>
      ) : (
        <>
          {/* Stats Cards */}
          {stats && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <Card>
                  <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700}>{stats.total || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">Total Stock</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <Card sx={{ borderTop: 3, borderColor: 'success.main' }}>
                  <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700} color="success.main">{stats.available || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">Disponíveis</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <Card sx={{ borderTop: 3, borderColor: 'primary.main' }}>
                  <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700} color="primary.main">{stats.active || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">Ativas</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <Card sx={{ borderTop: 3, borderColor: 'error.main' }}>
                  <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700} color="error.main">{stats.blocked || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">Bloqueadas</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <Card sx={{ borderTop: 3, borderColor: 'info.main' }}>
                  <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700} color="info.main">{stats.assigned || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">Atribuídas</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <Card>
                  <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700}>{((stats.lost || 0) + (stats.returned || 0))}</Typography>
                    <Typography variant="caption" color="text.secondary">Perdidas/Devol.</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Table */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>NFC UID</TableCell>
                      <TableCell>Serial</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Utilizador</TableCell>
                      <TableCell align="right">Saldo</TableCell>
                      <TableCell>Data Ativação</TableCell>
                      <TableCell align="center">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wristbands.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary" sx={{ py: 3 }}>
                            Nenhuma pulseira registada. Comece por importar um lote de UIDs.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      wristbands.map((wb) => (
                        <TableRow key={wb.id}>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{wb.nfc_uid}</TableCell>
                          <TableCell>{wb.nfc_serial || '-'}</TableCell>
                          <TableCell>
                            <Chip label={wb.status} size="small" color={STATUS_COLORS[wb.status || 'AVAILABLE'] || 'default'} />
                          </TableCell>
                          <TableCell>
                            {wb.participante_nome || wb.participante_email || wb.user_id || '-'}
                          </TableCell>
                          <TableCell align="right">
                            {(wb.saldo || 0).toLocaleString()} CVE
                          </TableCell>
                          <TableCell>
                            {wb.ativada_at ? new Date(wb.ativada_at).toLocaleString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}
                          </TableCell>
                          <TableCell align="center">
                            {wb.status === 'AVAILABLE' && (
                              <Tooltip title="Remover do stock">
                                <IconButton size="small" color="error" onClick={() => handleDelete(wb.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Por página:"
              />
            </>
          )}
        </>
      )}

      {/* Single Wristband Dialog */}
      <Dialog open={singleDialogOpen} onClose={() => setSingleDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Registar Pulseira NFC</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="NFC UID"
              value={singleForm.nfc_uid}
              onChange={(e) => setSingleForm({ ...singleForm, nfc_uid: e.target.value.toUpperCase() })}
              required
              placeholder="Ex: 04A1B2C3D4E5F6"
              helperText="Identificador único da tag NFC"
            />
            <TextField
              label="Número de Série (opcional)"
              value={singleForm.nfc_serial}
              onChange={(e) => setSingleForm({ ...singleForm, nfc_serial: e.target.value })}
              placeholder="Ex: PG-0001"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSingleDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateSingle} disabled={saving || !singleForm.nfc_uid}>
            {saving ? <CircularProgress size={20} /> : 'Registar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Import Dialog */}
      <Dialog open={batchDialogOpen} onClose={() => setBatchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Importar Lote de Pulseiras</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="info" sx={{ mb: 1 }}>
              Introduza um NFC UID por linha. Opcionalmente, separe UID e número de série com vírgula.
              <br />Exemplo: <code>04A1B2C3D4E5F6, PG-0001</code>
            </Alert>
            <TextField
              multiline
              rows={10}
              label="NFC UIDs (um por linha)"
              value={batchText}
              onChange={(e) => setBatchText(e.target.value)}
              placeholder={"04A1B2C3D4E5F6\n04A1B2C3D4E5F7\n04A1B2C3D4E5F8, PG-0003"}
              sx={{ fontFamily: 'monospace' }}
            />
            <Typography variant="body2" color="text.secondary">
              {batchText.split('\n').filter(l => l.trim()).length} UIDs detectados
            </Typography>
            {batchResult && (
              <Alert severity={batchResult.created > 0 ? 'success' : 'warning'}>
                Resultado: {batchResult.created} criadas, {batchResult.duplicates} duplicadas
                {batchResult.errors?.length > 0 && (
                  <Box sx={{ mt: 1 }}>Erros: {batchResult.errors.join(', ')}</Box>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDialogOpen(false)}>Fechar</Button>
          <Button variant="contained" onClick={handleCreateBatch} disabled={saving || !batchText.trim()}>
            {saving ? <CircularProgress size={20} /> : 'Importar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// ============================================
// VENDORS MANAGER COMPONENT
// ============================================

interface VendorsManagerProps {
  events: Event[];
}

const VendorsManager: React.FC<VendorsManagerProps> = ({ events: _eventsProp }) => {
  const [allEvents, setAllEvents] = useState<Event[]>(_eventsProp);
  useEffect(() => {
    eventsService.getEvents({ page: 1, limit: 500 }).then((r: any) => {
      if (r.data?.length) setAllEvents(r.data);
    }).catch(() => {});
  }, []);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    nome: '',
    descricao: '',
    categoria: 'FOOD' as string,
    localizacao: '',
    responsavel_nome: '',
    responsavel_email: '',
    responsavel_telefone: '',
    comissao_percentual: '',
    status: 'PENDING' as string,
  });

  const availableEvents = allEvents.filter((e) => e.status === 'PUBLISHED' || e.status === 'DRAFT');

  const loadVendors = useCallback(async (eventId: number) => {
    setLoading(true);
    try {
      const result = await eventsService.getVendors(eventId);
      setVendors(result.data || []);
    } catch (err) {
      console.error('Error loading vendors:', err);
      setVendors([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      loadVendors(selectedEventId);
    }
  }, [selectedEventId, loadVendors]);

  const resetForm = () => {
    setForm({
      nome: '', descricao: '', categoria: 'FOOD', localizacao: '',
      responsavel_nome: '', responsavel_email: '', responsavel_telefone: '',
      comissao_percentual: '', status: 'PENDING',
    });
    setEditingVendor(null);
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (vendor: Vendor) => {
    setEditingVendor(vendor);
    setForm({
      nome: vendor.nome || '',
      descricao: vendor.descricao || '',
      categoria: vendor.categoria || 'FOOD',
      localizacao: vendor.localizacao || '',
      responsavel_nome: vendor.responsavel_nome || '',
      responsavel_email: vendor.responsavel_email || '',
      responsavel_telefone: vendor.responsavel_telefone || '',
      comissao_percentual: vendor.comissao_percentual != null ? String(vendor.comissao_percentual) : '',
      status: vendor.status || 'PENDING',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedEventId || !form.nome) return;
    setSaving(true);
    try {
      const data: Partial<Vendor> = {
        nome: form.nome,
        descricao: form.descricao || undefined,
        categoria: form.categoria as Vendor['categoria'],
        localizacao: form.localizacao || undefined,
        responsavel_nome: form.responsavel_nome || undefined,
        responsavel_email: form.responsavel_email || undefined,
        responsavel_telefone: form.responsavel_telefone || undefined,
        comissao_percentual: form.comissao_percentual ? parseFloat(form.comissao_percentual) : undefined,
        status: form.status as Vendor['status'],
      };

      if (editingVendor) {
        await eventsService.updateVendor(selectedEventId, editingVendor.id, data);
      } else {
        await eventsService.createVendor(selectedEventId, data);
      }
      setDialogOpen(false);
      resetForm();
      loadVendors(selectedEventId);
    } catch (err) {
      console.error('Error saving vendor:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vendorId: number) => {
    if (!selectedEventId || !window.confirm('Eliminar este vendedor?')) return;
    try {
      await eventsService.deleteVendor(selectedEventId, vendorId);
      loadVendors(selectedEventId);
    } catch (err) {
      console.error('Error deleting vendor:', err);
    }
  };

  const selectedEvent = availableEvents.find((e) => e.id === selectedEventId);

  const getCategoryLabel = (cat?: string) => {
    const found = VENDOR_CATEGORIES.find((c) => c.value === cat);
    return found?.label || cat || '-';
  };

  const getStatusChip = (status?: string) => {
    const found = VENDOR_STATUS_OPTIONS.find((s) => s.value === status);
    if (found) {
      return <Chip size="small" color={found.color} label={found.label} />;
    }
    return <Chip size="small" color="default" label={status || '-'} />;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>Gestão de Vendedores</Typography>
        {selectedEventId && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Novo Vendedor
          </Button>
        )}
      </Box>

      {/* Event Selector */}
      <TextField
        select
        fullWidth
        label="Selecionar Evento"
        value={selectedEventId || ''}
        onChange={(e) => setSelectedEventId(Number(e.target.value))}
        sx={{ mb: 3 }}
      >
        <MenuItem value="">-- Selecionar evento --</MenuItem>
        {availableEvents.map((evt) => (
          <MenuItem key={evt.id} value={evt.id}>
            {evt.titulo} ({evt.status})
          </MenuItem>
        ))}
      </TextField>

      {!selectedEventId && (
        <Alert severity="info">Selecione um evento acima para gerir os vendedores.</Alert>
      )}

      {selectedEventId && loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {selectedEventId && !loading && (
        <>
          {selectedEvent && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>{selectedEvent.titulo}</strong> — {selectedEvent.local_cidade || 'Local não definido'}
              {selectedEvent.data_inicio && ` | ${new Date(selectedEvent.data_inicio).toLocaleDateString('pt-CV', { timeZone: 'UTC' })}`}
            </Alert>
          )}

          {/* Vendors Table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell><strong>Nome</strong></TableCell>
                  <TableCell><strong>Categoria</strong></TableCell>
                  <TableCell><strong>Localização</strong></TableCell>
                  <TableCell align="center"><strong>Comissão %</strong></TableCell>
                  <TableCell align="right"><strong>Total Vendas</strong></TableCell>
                  <TableCell align="center"><strong>Estado</strong></TableCell>
                  <TableCell align="center"><strong>Acções</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vendors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        Nenhum vendedor. Clique em "Novo Vendedor" para criar.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  vendors.map((vendor) => (
                    <TableRow key={vendor.id} hover>
                      <TableCell>
                        <Typography fontWeight={600} fontSize={14}>{vendor.nome}</Typography>
                        {vendor.descricao && (
                          <Typography variant="caption" color="text.secondary">{vendor.descricao}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" label={getCategoryLabel(vendor.categoria)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {vendor.localizacao || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {vendor.comissao_percentual != null ? `${vendor.comissao_percentual}%` : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600}>
                          {vendor.total_vendas != null
                            ? new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', minimumFractionDigits: 0 }).format(vendor.total_vendas)
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {getStatusChip(vendor.status)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(vendor)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleDelete(vendor.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Create/Edit Vendor Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingVendor ? 'Editar Vendedor' : 'Novo Vendedor'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nome *"
              fullWidth
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              placeholder="Ex: Bar Principal, Stand Merch"
            />
            <TextField
              label="Descrição"
              fullWidth
              multiline
              rows={2}
              value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select
                label="Categoria"
                fullWidth
                value={form.categoria}
                onChange={(e) => setForm({ ...form, categoria: e.target.value })}
              >
                {VENDOR_CATEGORIES.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Estado"
                fullWidth
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {VENDOR_STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </TextField>
            </Box>
            <TextField
              label="Localização"
              fullWidth
              value={form.localizacao}
              onChange={(e) => setForm({ ...form, localizacao: e.target.value })}
              placeholder="Ex: Zona A, Entrada Principal"
            />
            <TextField
              label="Comissão (%)"
              type="number"
              fullWidth
              value={form.comissao_percentual}
              onChange={(e) => setForm({ ...form, comissao_percentual: e.target.value })}
              placeholder="Ex: 10"
              helperText="Percentagem de comissão sobre vendas"
            />
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary">Contacto do Responsável</Typography>
            <TextField
              label="Nome do Responsável"
              fullWidth
              value={form.responsavel_nome}
              onChange={(e) => setForm({ ...form, responsavel_nome: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={form.responsavel_email}
                onChange={(e) => setForm({ ...form, responsavel_email: e.target.value })}
              />
              <TextField
                label="Telefone"
                fullWidth
                value={form.responsavel_telefone}
                onChange={(e) => setForm({ ...form, responsavel_telefone: e.target.value })}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.nome}
          >
            {saving ? <CircularProgress size={20} /> : editingVendor ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// ============================================
// PROMO CODES MANAGER
// ============================================

const DISCOUNT_TYPES = [
  { value: 'PERCENTAGE', label: 'Percentagem (%)' },
  { value: 'FIXED', label: 'Valor Fixo (CVE)' },
  { value: 'FREE_TICKET', label: 'Bilhete Grátis' },
];

const PROMO_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Activo', color: 'success' as const },
  { value: 'INACTIVE', label: 'Inactivo', color: 'default' as const },
  { value: 'EXPIRED', label: 'Expirado', color: 'error' as const },
];

interface PromoCodesManagerProps {
  events: Event[];
}

const PromoCodesManager: React.FC<PromoCodesManagerProps> = ({ events: _eventsProp }) => {
  const [allEvents, setAllEvents] = useState<Event[]>(_eventsProp);
  useEffect(() => {
    eventsService.getEvents({ page: 1, limit: 500 }).then((r: any) => {
      if (r.data?.length) setAllEvents(r.data);
    }).catch(() => {});
  }, []);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState<PromoCode | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    codigo: '',
    nome: '',
    descricao: '',
    tipo_desconto: 'PERCENTAGE' as string,
    valor_desconto: '',
    max_usos: '',
    max_usos_por_utilizador: '1',
    valor_minimo_pedido: '',
    desconto_maximo: '',
    valido_de: '',
    valido_ate: '',
    status: 'ACTIVE' as string,
  });

  const availableEvents = allEvents.filter((e) => e.status === 'PUBLISHED' || e.status === 'DRAFT');

  const loadPromoCodes = useCallback(async (eventId: number) => {
    setLoading(true);
    try {
      const result = await eventsService.getPromoCodes(eventId);
      setPromoCodes(result.data || []);
    } catch (err) {
      console.error('Error loading promo codes:', err);
      setPromoCodes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      loadPromoCodes(selectedEventId);
    }
  }, [selectedEventId, loadPromoCodes]);

  const resetForm = () => {
    setForm({
      codigo: '', nome: '', descricao: '', tipo_desconto: 'PERCENTAGE',
      valor_desconto: '', max_usos: '', max_usos_por_utilizador: '1',
      valor_minimo_pedido: '', desconto_maximo: '', valido_de: '', valido_ate: '',
      status: 'ACTIVE',
    });
    setEditingPromo(null);
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (promo: PromoCode) => {
    setEditingPromo(promo);
    setForm({
      codigo: promo.codigo || '',
      nome: promo.nome || '',
      descricao: promo.descricao || '',
      tipo_desconto: promo.tipo_desconto || 'PERCENTAGE',
      valor_desconto: promo.valor_desconto != null ? String(promo.valor_desconto) : '',
      max_usos: promo.max_usos != null ? String(promo.max_usos) : '',
      max_usos_por_utilizador: promo.max_usos_por_utilizador != null ? String(promo.max_usos_por_utilizador) : '1',
      valor_minimo_pedido: promo.valor_minimo_pedido != null ? String(promo.valor_minimo_pedido) : '',
      desconto_maximo: promo.desconto_maximo != null ? String(promo.desconto_maximo) : '',
      valido_de: promo.valido_de ? promo.valido_de.slice(0, 16) : '',
      valido_ate: promo.valido_ate ? promo.valido_ate.slice(0, 16) : '',
      status: promo.status || 'ACTIVE',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedEventId || !form.codigo || !form.valor_desconto) return;
    setSaving(true);
    try {
      const data: Partial<PromoCode> = {
        codigo: form.codigo.toUpperCase(),
        nome: form.nome || undefined,
        descricao: form.descricao || undefined,
        tipo_desconto: form.tipo_desconto as PromoCode['tipo_desconto'],
        valor_desconto: parseFloat(form.valor_desconto),
        max_usos: form.max_usos ? parseInt(form.max_usos) : undefined,
        max_usos_por_utilizador: form.max_usos_por_utilizador ? parseInt(form.max_usos_por_utilizador) : undefined,
        valor_minimo_pedido: form.valor_minimo_pedido ? parseFloat(form.valor_minimo_pedido) : undefined,
        desconto_maximo: form.desconto_maximo ? parseFloat(form.desconto_maximo) : undefined,
        valido_de: form.valido_de || undefined,
        valido_ate: form.valido_ate || undefined,
        status: form.status as PromoCode['status'],
      };

      // API only has create for promo codes based on the service
      await eventsService.createPromoCode(selectedEventId, data);
      setDialogOpen(false);
      resetForm();
      loadPromoCodes(selectedEventId);
    } catch (err) {
      console.error('Error saving promo code:', err);
    } finally {
      setSaving(false);
    }
  };

  const selectedEvent = availableEvents.find((e) => e.id === selectedEventId);

  const getDiscountLabel = (tipo?: string) => {
    const found = DISCOUNT_TYPES.find((d) => d.value === tipo);
    return found?.label || tipo || '-';
  };

  const formatDiscountValue = (promo: PromoCode) => {
    if (promo.tipo_desconto === 'PERCENTAGE') return `${promo.valor_desconto}%`;
    if (promo.tipo_desconto === 'FIXED') {
      return new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', minimumFractionDigits: 0 }).format(promo.valor_desconto);
    }
    if (promo.tipo_desconto === 'FREE_TICKET') return 'Grátis';
    return String(promo.valor_desconto);
  };

  const getStatusChip = (status?: string) => {
    const found = PROMO_STATUS_OPTIONS.find((s) => s.value === status);
    if (found) {
      return <Chip size="small" color={found.color} label={found.label} />;
    }
    return <Chip size="small" color="default" label={status || '-'} />;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>Códigos Promocionais</Typography>
        {selectedEventId && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Novo Código Promo
          </Button>
        )}
      </Box>

      {/* Event Selector */}
      <TextField
        select
        fullWidth
        label="Selecionar Evento"
        value={selectedEventId || ''}
        onChange={(e) => setSelectedEventId(Number(e.target.value))}
        sx={{ mb: 3 }}
      >
        <MenuItem value="">-- Selecionar evento --</MenuItem>
        {availableEvents.map((evt) => (
          <MenuItem key={evt.id} value={evt.id}>
            {evt.titulo} ({evt.status})
          </MenuItem>
        ))}
      </TextField>

      {!selectedEventId && (
        <Alert severity="info">Selecione um evento acima para gerir os códigos promocionais.</Alert>
      )}

      {selectedEventId && loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {selectedEventId && !loading && (
        <>
          {selectedEvent && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>{selectedEvent.titulo}</strong> — {selectedEvent.local_cidade || 'Local não definido'}
              {selectedEvent.data_inicio && ` | ${new Date(selectedEvent.data_inicio).toLocaleDateString('pt-CV', { timeZone: 'UTC' })}`}
            </Alert>
          )}

          {/* Promo Codes Table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell><strong>Código</strong></TableCell>
                  <TableCell><strong>Nome</strong></TableCell>
                  <TableCell><strong>Tipo Desconto</strong></TableCell>
                  <TableCell align="center"><strong>Valor</strong></TableCell>
                  <TableCell align="center"><strong>Usos</strong></TableCell>
                  <TableCell><strong>Validade</strong></TableCell>
                  <TableCell align="center"><strong>Estado</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {promoCodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        Nenhum código promocional. Clique em "Novo Código Promo" para criar.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  promoCodes.map((promo) => (
                    <TableRow key={promo.id} hover>
                      <TableCell>
                        <Chip
                          label={promo.codigo}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ fontWeight: 700, fontFamily: 'monospace', letterSpacing: 1 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{promo.nome || '-'}</Typography>
                        {promo.descricao && (
                          <Typography variant="caption" color="text.secondary">{promo.descricao}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{getDiscountLabel(promo.tipo_desconto)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography fontWeight={600} color="primary">
                          {formatDiscountValue(promo)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {promo.usos_atuais || 0} / {promo.max_usos || '---'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {promo.valido_de || promo.valido_ate ? (
                          <Typography variant="caption" color="text.secondary">
                            {promo.valido_de ? new Date(promo.valido_de).toLocaleDateString('pt-CV', { timeZone: 'UTC' }) : '...'}{' '}
                            - {promo.valido_ate ? new Date(promo.valido_ate).toLocaleDateString('pt-CV', { timeZone: 'UTC' }) : '...'}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">Sem limite</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {getStatusChip(promo.status)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Create Promo Code Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPromo ? 'Editar Código Promo' : 'Novo Código Promocional'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Código *"
                fullWidth
                value={form.codigo}
                onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })}
                placeholder="Ex: CVMA2026, VIP20"
                helperText="Será convertido para maiúsculas"
                slotProps={{ htmlInput: { style: { textTransform: 'uppercase', fontFamily: 'monospace', letterSpacing: 2 } } }}
              />
              <TextField
                label="Nome"
                fullWidth
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Ex: Desconto Early Bird"
              />
            </Box>
            <TextField
              label="Descrição"
              fullWidth
              multiline
              rows={2}
              value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select
                label="Tipo de Desconto"
                fullWidth
                value={form.tipo_desconto}
                onChange={(e) => setForm({ ...form, tipo_desconto: e.target.value })}
              >
                {DISCOUNT_TYPES.map((dt) => (
                  <MenuItem key={dt.value} value={dt.value}>{dt.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                label={form.tipo_desconto === 'PERCENTAGE' ? 'Valor (%) *' : 'Valor (CVE) *'}
                type="number"
                fullWidth
                value={form.valor_desconto}
                onChange={(e) => setForm({ ...form, valor_desconto: e.target.value })}
                placeholder={form.tipo_desconto === 'PERCENTAGE' ? 'Ex: 20' : 'Ex: 500'}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Máx. Usos Total"
                type="number"
                fullWidth
                value={form.max_usos}
                onChange={(e) => setForm({ ...form, max_usos: e.target.value })}
                placeholder="Vazio = ilimitado"
              />
              <TextField
                label="Máx. por Utilizador"
                type="number"
                fullWidth
                value={form.max_usos_por_utilizador}
                onChange={(e) => setForm({ ...form, max_usos_por_utilizador: e.target.value })}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Valor Mínimo do Pedido"
                type="number"
                fullWidth
                value={form.valor_minimo_pedido}
                onChange={(e) => setForm({ ...form, valor_minimo_pedido: e.target.value })}
                placeholder="Vazio = sem mínimo"
              />
              <TextField
                label="Desconto Máximo (CVE)"
                type="number"
                fullWidth
                value={form.desconto_maximo}
                onChange={(e) => setForm({ ...form, desconto_maximo: e.target.value })}
                placeholder="Vazio = sem limite"
                helperText="Limite para descontos %"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Válido De"
                type="datetime-local"
                fullWidth
                value={form.valido_de}
                onChange={(e) => setForm({ ...form, valido_de: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                label="Válido Até"
                type="datetime-local"
                fullWidth
                value={form.valido_ate}
                onChange={(e) => setForm({ ...form, valido_ate: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Box>
            <TextField
              select
              label="Estado"
              fullWidth
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              {PROMO_STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.codigo || !form.valor_desconto}
          >
            {saving ? <CircularProgress size={20} /> : editingPromo ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// ── Hero Slides Tab (wrapper for embedding) ───────────────────────────────────

const HeroSlidesTab: React.FC = () => (
  <Box sx={{ mt: 1 }}>
    <HeroSlidesPage />
  </Box>
);

export default Events;
